import { ThreadChannel, ThreadChannelTask } from '@@types/Channel';
import { CHANNEL_ID, StringTMap, THREAD_CHANNEL_ID } from '@@types/common';

import { PANEL_TYPE } from '~/constants';
import ChannelsStore from '~/stores/ChannelsStore';
import { mainDetailUiState, threadDetailUiState } from '~/stores/DetailUiState';
import { uiState } from '~/stores/UiState';
import { userStore } from '~/stores/UserStore';
import { action, computed, observable } from 'mobx';
import { complement, equals, prop, propEq } from 'ramda';

class ThreadChannelStore extends ChannelsStore<ThreadChannel> {
  @observable
  mainThreadChannelIdList: THREAD_CHANNEL_ID[] = [];

  @observable
  lastReadChannelId: THREAD_CHANNEL_ID | null = null;

  @observable.shallow
  taskListMap: Record<string, ThreadChannelTask[]> = {};

  mainThreadChannelAllLoaded = false;

  mainThreadChannelPage = 0;

  @computed
  get mainThreadChannels() {
    return this.mainThreadChannelIdList.map(this.getById);
  }

  @action
  public clearMainChannelList = () => {
    this.mainThreadChannelIdList = [];
    this.lastReadChannelId = null;
  };

  @action
  public mergeMainChannelList = (channelList: ThreadChannel[]) => {
    this.mainThreadChannelIdList = [
      ...this.mainThreadChannelIdList,
      ...channelList.map((channel) => channel.channelId),
    ];
    this.mergeChannels(channelList);

    if (!this.lastReadChannelId) {
      const lastReadChannelId = this.mainThreadChannelIdList.find((channelId) => !this.getById(channelId)?.unreadFlag);
      if (lastReadChannelId !== this.mainThreadChannelIdList[0]) {
        this.lastReadChannelId = lastReadChannelId;
      }
    }
  };

  @action
  public removeThreadChannelInMainThreadChannel = (channelId: THREAD_CHANNEL_ID) => {
    const index = this.mainThreadChannelIdList.findIndex(equals(channelId));
    if (index > -1) {
      this.mainThreadChannelIdList.splice(index, 1);
      this.mainThreadChannelIdList = this.mainThreadChannelIdList.slice();
    }
  };

  @action
  public handleLeaveChannelInMainThreadChannel = (channelId: CHANNEL_ID) => {
    this.mainThreadChannelIdList = this.mainThreadChannels
      .filter(complement(propEq('parentChannelId', channelId)))
      .map(prop('channelId'));
  };

  @action
  deleteChannel = (channelId) => {
    const index = this.mainThreadChannelIdList.indexOf(channelId);
    if (index > -1) {
      this.mainThreadChannelIdList.splice(index, 1);
      this.mainThreadChannelIdList = this.mainThreadChannelIdList.slice();
    }
    delete this.data[channelId];
  };

  public setMainThreadChannelAllLoaded = (value: boolean) => {
    this.mainThreadChannelAllLoaded = value;
  };

  setMainThreadChannelPage = (page: number) => {
    this.mainThreadChannelPage = page;
  };

  public getById = (id: THREAD_CHANNEL_ID): ThreadChannel => {
    return this.data[id];
  };

  @action
  public clearUnusedData = () => {
    if (uiState.panelType !== PANEL_TYPE.NONE) {
      return;
    }
    const mainChannelId = mainDetailUiState.contentsChannelId;
    this.data = Object.keys(this.data).reduce((acc: StringTMap<ThreadChannel>, channelId: THREAD_CHANNEL_ID) => {
      const channel = this.data[channelId];
      if (channelId === threadDetailUiState.contentsChannelId || channel.parentChannelId === mainChannelId) {
        acc[channelId] = channel;
      }
      return acc;
    }, {});
  };

  @computed
  get threadLayerChannel() {
    return this.getById(threadDetailUiState.contentsChannelId);
  }

  public getParentChannelId = (channelId: CHANNEL_ID): CHANNEL_ID | null => {
    return this.getById(channelId)?.parentChannelId ?? null;
  };

  public isFollow = (channelId: CHANNEL_ID) => {
    const channel = this.getById(channelId);
    if (!channel) {
      return false;
    }
    return this.isFollowChannel(channel);
  };

  protected isFollowChannel = (channel: ThreadChannel) => {
    return channel.members?.includes(userStore.id) ?? false;
  };

  @action
  public setThreadChannelTasks = (channelId: THREAD_CHANNEL_ID, tasks: ThreadChannelTask[]) => {
    this.taskListMap = uiState.pickAndSetLruChannelIds(channelId, tasks)(this.taskListMap);
  };
}

export default ThreadChannelStore;

export const threadChannelStore = new ThreadChannelStore();
